/**
 * currentPhysicianRows.js — PROD-1020: after a case is reactivated and sent to a new physician, the
 * appointment rows of the PREVIOUS physician must not stand in for the new physician's initial visit.
 *
 * orella-api reactivate_case clears the case's physician (doctor_id / target_doctor_id = NULL), keeps
 * previous_doctor_id = the old physician and leaves the old appointment rows in place as history.
 * get_cases serves case.appointments = every row except cancelled / rejected (each row carries
 * doctor_id), oldest first, so the list still starts with the old physician's completed / no_show
 * rows. Case Home's originalAppointment took the FIRST initial row: the "Your visit" line pointed at
 * the old physician's visit (wrong id, wrong date, wrong status).
 *
 * rowsOfCurrentPhysician mirrors the API's _reactivated_pick_pool (current physician only) and only
 * acts on a reactivated case (caseItem.previousDoctorId set). Any other case is returned untouched.
 * Rows without a doctor_id (legacy, local) are kept.
 *
 * initialRowFor picks the row that stands for the initial visit: a pending_reschedule initial row
 * wins over an earlier confirmed one (PROD-921 / PROD-1008: the reschedule is the live request).
 *
 * Q-1 (open with Alex): the old physician's COMPLETED visits stay listed as "Completed visit" lines
 * (CaseHome completedAppointments). To hide them too, filter completedAppointments through
 * rowsOfCurrentPhysician(currentCase, allAppointments).
 *
 * Import-free on purpose: the gate executes it.
 */

const idOf = (v) => (v === undefined || v === null ? '' : String(v))

/** The rows that belong to the case's current physician; all rows when the case was never reactivated. */
export function rowsOfCurrentPhysician(caseItem, rows) {
  const list = Array.isArray(rows) ? rows : []
  if (!caseItem || typeof caseItem !== 'object' || !idOf(caseItem.previousDoctorId)) return list
  const current = idOf(caseItem.sentToDoctor || caseItem.doctor_id || caseItem.doctorId)
  return list.filter((row) => {
    const owner = idOf(row?.doctor_id || row?.doctorId)
    return owner === '' || (current !== '' && owner === current)
  })
}

/** The initial-visit row to bind to the request: a pending_reschedule row first, else the first row. */
export function initialRowFor(rows) {
  const list = Array.isArray(rows) ? rows : []
  return list.find((row) => row?.status === 'pending_reschedule') || list[0]
}
